import React from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { LEAD_TYPE_BADGE, CHANNEL_LABELS, CHANNEL_COLORS } from '../../lib/leadMeta';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const Button = React.forwardRef(
  (
    { className, variant = 'primary', size = 'md', isLoading, children, type = 'button', disabled, ...props },
    ref
  ) => {
    const baseStyles = "inline-flex items-center justify-center rounded-[10px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary border border-transparent disabled:cursor-not-allowed disabled:opacity-40 disabled:border-lineGray";
    const variants = {
      primary: "bg-primary text-white hover:bg-primary-dark border-transparent",
      secondary: "bg-white text-navy border-[1.5px] border-navy hover:bg-ivory",
      outline: "bg-white text-navy border-[1.5px] border-navy hover:bg-ivory",
      ghost: "hover:bg-ivory text-navy border-transparent",
      danger: "bg-red-500 text-white hover:bg-red-600 border-transparent"
    };
    const sizes = {
      sm: "h-8 px-3 text-xs",
      md: "h-10 px-4 py-2 text-sm",
      lg: "h-12 px-8 text-base"
    };

    return (
      <button
        ref={ref}
        type={type}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        {...props}
        disabled={isLoading || disabled}
      >
        {isLoading ? <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"/> : null}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export const Input = React.forwardRef(({ className, label, error, ...props }, ref) => {
  return (
    <div className="w-full">
      {label && <label className="block text-sm font-medium text-navy mb-1">{label}</label>}
      <input
        ref={ref}
        className={cn(
          "flex h-10 w-full rounded-[10px] border border-lineGray bg-white px-3 py-2 text-sm placeholder:text-slateText focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:cursor-not-allowed disabled:opacity-40 disabled:border-lineGray transition-shadow",
          error && "border-red-500 focus:ring-red-500",
          className
        )}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
});

export const Select = React.forwardRef(({ className, label, error, options, ...props }, ref) => {
  return (
    <div className="w-full">
      {label && <label className="block text-sm font-medium text-navy mb-1">{label}</label>}
      <select
        ref={ref}
        className={cn(
          "flex h-10 w-full rounded-[10px] border border-lineGray bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:cursor-not-allowed disabled:opacity-40 disabled:border-lineGray",
          error && "border-red-500 focus:ring-red-500",
          className
        )}
        {...props}
      >
        {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
});

export const Card = ({ className, children, ...props }) => (
  <div className={cn("rounded-xl border border-lineGray bg-white text-gray-950", className)} {...props}>
    {children}
  </div>
);

export const Badge = ({ className, variant = 'default', children }) => {
  const variants = {
    default: "bg-skyTint text-navy",
    primary: "bg-skyTint text-navy",
    success: "bg-successBg text-success",
    warning: "bg-warningBg text-warning",
    danger: "bg-red-100 text-red-800",
    purple: "bg-purple-100 text-purple-800",
    teal: "bg-teal-100 text-teal-800",
    locked: "bg-lockedBg text-locked",
  };
  return (
    <span className={cn("inline-flex items-center rounded-[100px] px-2.5 py-0.5 text-xs font-semibold", variants[variant], className)}>
      {children}
    </span>
  );
}

export const getChannelBadge = (channel) => {
  if (!channel || !CHANNEL_LABELS[channel]) return null;
  return (
    <span
      className="inline-flex items-center rounded-[100px] px-2.5 py-0.5 text-xs font-semibold text-white"
      style={{ backgroundColor: CHANNEL_COLORS[channel] }}
    >
      {CHANNEL_LABELS[channel]}
    </span>
  );
};

export const getLeadTypeBadge = (type) => {
  const config = LEAD_TYPE_BADGE[type] || LEAD_TYPE_BADGE.fresh;
  if (config.color) {
    return (
      <span
        className="inline-flex items-center rounded-[100px] px-2.5 py-0.5 text-xs font-semibold"
        style={{ backgroundColor: config.color, color: config.textColor || '#ffffff' }}
      >
        {config.label}
      </span>
    );
  }
  return <Badge variant={config.variant}>{config.label}</Badge>;
};